import React, { useState } from 'react'
import { Toaster } from 'react-hot-toast'
import { Breadcrumb } from '../../../../../components/ui/breadcrumb'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import axios from '../../../../../lib/axios'
import useAuth from '../../../../../hooks/useAuth'
import { Tablist, TablistItem } from '../../../../../components/ui/tablist'
import Denom from './detail/Denom'
import { isInterface } from '../../../../../lib/utils'
import SpinnerFeedback from '../../../../../components/portal/feedback/SpinnerFeedback'
import ErrorFeedback from '../../../../../components/portal/feedback/ErrorFeedback'
import { createDefaultCustomerDenom } from '../../../../../lib/fetch/customerDenom'
import { useTranslation } from 'react-i18next'

const Detail = () => {
    const { customerid } = useParams()
    const { auth } = useAuth()
    const { t } = useTranslation()
    const navigate = useNavigate()
    const location = useLocation()

    const customer: Customer | null = location.state?.customer ?? null

    const [selected_subpage, setselected_subpage] = useState<'denom' | 'order'>('denom')

    const { data: denom, isFetching: denom_isFetching, refetch: refetch_denom } = useQuery<CustomerDenom | null>({
        queryKey: [`customer_denom_${customerid}`],
        queryFn: async () => {
            const cid = parseFloat(customerid || '0')
            try {
                const { data } = await axios.get('customer/denom/search', {
                    params: { token: auth.data?.token, customerid: cid }
                })
                console.log(`[SERVER RESPONSE: customer/denom/search] customerid=${cid}`, data)
                const candidates = [
                    data,
                    data?.object,
                    data?.object?.[0],
                    data?.object?.[0]?.denom,
                    data?.denom,
                ]
                const raw = candidates.find((c) => c && Array.isArray(c.coins) && Array.isArray(c.notes)) ?? null

                const mapItem = (item: any): CDenom => ({
                    customerid: cid,
                    id:         item.apicustomerdenomid ?? item.id ?? 0,
                    coupure:    item.coupure,
                    minqty:     item.minqty ?? 0,
                    maxqty:     item.maxqty ?? 0,
                    perqty:     item.perquantity ?? item.perqty ?? 0,
                })
                const coins: CDenom[] = raw ? raw.coins.filter((c: any) => c.coupure !== 0).map(mapItem) : []
                const notes: CDenom[] = raw ? raw.notes.filter((n: any) => n.coupure !== 0).map(mapItem) : []

                if (coins.length > 0 || notes.length > 0) {
                    coins.sort((a, b) => b.coupure - a.coupure)
                    notes.sort((a, b) => b.coupure - a.coupure)
                    return {
                        customerid:    cid,
                        ordercoins:    !!raw.ordercoins,
                        ordernotes:    !!raw.ordernotes,
                        announcecoins: !!raw.announcecoins,
                        announcenotes: !!raw.announcenotes,
                        coins,
                        notes,
                    }
                }

                const defaultDenom = await createDefaultCustomerDenom(cid, auth)
                defaultDenom.coins.sort((a, b) => b.coupure - a.coupure)
                defaultDenom.notes.sort((a, b) => b.coupure - a.coupure)
                return defaultDenom
            } catch (error) {
                console.log(error)
                try {
                    const defaultDenom = await createDefaultCustomerDenom(cid, auth)
                    defaultDenom.coins.sort((a, b) => b.coupure - a.coupure)
                    defaultDenom.notes.sort((a, b) => b.coupure - a.coupure)
                    return defaultDenom
                } catch {
                    return null
                }
            }
        }
    })

    return (
        <>
            <Toaster />
            <div className='overflow-auto relative w-full p-4 md:p-8'>
                <div className='flex flex-col'>
                    <Breadcrumb items={[
                        { title: <p onClick={() => { navigate('/instellingen') }} className='cursor-pointer'>{t('settings.title')}</p> },
                        { title: <p onClick={() => { navigate('..') }} className='cursor-pointer'>{t('settings.customer_settings_title')}</p> },
                        { title: `${t('settings.customer')} ${customer?.name ?? customerid}` }
                    ]} />
                    <h1 className='text-2xl font-semibold'>{t('settings.customer_settings_title')}</h1>
                </div>

                <div className='flex border flex-col gap-4 rounded-sm p-4 mt-4 md:mt-8'>
                    <h2 className='text-lg'>{t('settings.title_detail')}</h2>
                    <div className='grid grid-cols-[repeat(auto-fit,minmax(250px,1fr))] gap-2'>
                        <div className='flex gap-2'>
                            <p className='font-semibold'>{t('settings.relation_nr')}</p>
                            <p className='text-neutral-500'>{customerid}</p>
                        </div>
                        {customer && (
                            <div className='flex gap-2'>
                                <p className='font-semibold'>{t('settings.customer')}</p>
                                <p className='text-neutral-500'>{customer.name}</p>
                            </div>
                        )}
                    </div>
                </div>

                <div className='flex border flex-col gap-4 rounded-sm py-4 mt-4 md:mt-8'>
                    <Tablist value={selected_subpage} onValueChange={(value) => { setselected_subpage(value as 'denom' | 'order') }}>
                        <TablistItem value='denom'>{t('settings.denom')}</TablistItem>
                        <TablistItem value='order'>{t('settings.order')}</TablistItem>
                    </Tablist>

                    <div className='px-4'>
                        {selected_subpage === 'denom' && (
                            <>
                                {isInterface(denom, {} as CustomerDenom)
                                    ? <Denom denom={denom} refetch={() => { refetch_denom() }} />
                                    : <>{denom_isFetching ? <SpinnerFeedback /> : <ErrorFeedback />}</>
                                }
                            </>
                        )}

                        {selected_subpage === 'order' && (
                            <>
                                {isInterface(denom, {} as CustomerDenom)
                                    ? <Denom denom={denom} refetch={() => { refetch_denom() }} orderOnly />
                                    : <>{denom_isFetching ? <SpinnerFeedback /> : <ErrorFeedback />}</>
                                }
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    )
}

export default Detail
